import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { addHours, addDays } from 'date-fns'
import { Bell, Loader2 } from 'lucide-react'
import { reminderApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import type { CRMEntityType, CreateReminderRequest } from '@/types'

interface CreateReminderDialogProps {
  entityType?: CRMEntityType
  entityId?: string
  activityId?: string
  onSuccess?: () => void
  trigger?: React.ReactNode
}

const quickOptions = [
  { label: 'In 1 hour', getValue: () => addHours(new Date(), 1) },
  { label: 'In 3 hours', getValue: () => addHours(new Date(), 3) },
  { label: 'Tomorrow', getValue: () => addDays(new Date(), 1) },
  { label: 'In 1 week', getValue: () => addDays(new Date(), 7) },
]

export function CreateReminderDialog({
  entityType,
  entityId,
  activityId,
  onSuccess,
  trigger,
}: CreateReminderDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<Partial<CreateReminderRequest>>({})

  const createMutation = useMutation({
    mutationFn: (data: CreateReminderRequest) => reminderApi.createReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success(t('crm.reminderCreated', 'Reminder created'))
      setOpen(false)
      setFormData({})
      onSuccess?.()
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('messages.createError', 'Failed to create')))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title) {
      toast.error(t('validation.required', 'Title is required'))
      return
    }
    if (!formData.remind_at) {
      toast.error(t('validation.required', 'Reminder time is required'))
      return
    }

    createMutation.mutate({
      title: formData.title,
      description: formData.description,
      remind_at: formData.remind_at,
      entity_type: entityType,
      entity_id: entityId,
      activity_id: activityId,
    })
  }

  const handleQuickOption = (getValue: () => Date) => {
    setFormData({ ...formData, remind_at: getValue().toISOString() })
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setFormData({}) }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline">
            <Bell className="h-4 w-4 mr-2" />
            {t('crm.setReminder', 'Set Reminder')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('crm.createReminder', 'Create Reminder')}</DialogTitle>
          <DialogDescription>
            {t('crm.createReminderDescription', 'Set a reminder for this item')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">{t('crm.title', 'Title')} *</Label>
              <Input
                id="title"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('crm.reminderTitlePlaceholder', 'Follow up with...')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('crm.description', 'Description')}</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('crm.reminderDescriptionPlaceholder', 'Add notes...')}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('crm.quickOptions', 'Quick Options')}</Label>
              <div className="flex flex-wrap gap-2">
                {quickOptions.map((option) => (
                  <Button
                    key={option.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickOption(option.getValue)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="remind_at">{t('crm.remindAt', 'Remind At')} *</Label>
              <Input
                id="remind_at"
                type="datetime-local"
                value={formData.remind_at?.slice(0, 16) || ''}
                onChange={(e) => setFormData({ ...formData, remind_at: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CreateReminderDialog
